import { useEffect } from 'react';
import { SITE } from '../config/site';

const UPDATED = 'October 10, 2026';

function InfoLayout({ title, intro, children }) {
  useEffect(() => {
    document.title = `${title} · ${SITE.name}`;
    return () => { document.title = `${SITE.name} · Hand history analytics and settle-ups for home games`; };
  }, [title]);

  return (
    <div className="pn-page min-h-[100dvh]">
      <article className="mx-auto max-w-[72ch] px-4 pb-24 pt-32 sm:px-6 land:pt-40">
        <h1 className="pn-h2">{title}</h1>
        {intro && <p className="pn-lede mt-5 max-w-[60ch]">{intro}</p>}
        <div className="mt-12 space-y-10">{children}</div>
      </article>
    </div>
  );
}

function Block({ heading, children }) {
  return (
    <section>
      <h2 className="pn-h3">{heading}</h2>
      <div className="pn-body mt-3 space-y-3 [&_a]:text-pn-fg [&_a]:underline [&_a]:decoration-pn-line/30 [&_a]:underline-offset-4 [&_li]:ml-5 [&_li]:list-disc">
        {children}
      </div>
    </section>
  );
}

function PageLink({ page, setCurrentPage, children }) {
  return (
    <a
      href={`/${page}`}
      onClick={e => {
        e.preventDefault();
        setCurrentPage(page);
        window.scrollTo({ top: 0 });
      }}
    >
      {children}
    </a>
  );
}

const contactLine = SITE.contactEmail
  ? <>email <a href={`mailto:${SITE.contactEmail}`}>{SITE.contactEmail}</a></>
  : <>open an issue on <a href={`${SITE.github}/issues`} target="_blank" rel="noopener noreferrer">GitHub</a></>;

export function About({ setCurrentPage }) {
  return (
    <InfoLayout
      title="About"
      intro={`${SITE.name} turns PokerNow logs into hand-by-hand analytics and settle-ups. It is built and run by one person, ${SITE.founder}, as ${SITE.company}.`}
    >
      <Block heading="Where it came from">
        <p>
          It started as bankrollBuddy, a simple bankroll tracker. Home games on PokerNow produce detailed logs that
          nobody reads, so the next step was a parser that reads them for you: every hand, every player, every pot.
        </p>
      </Block>
      <Block heading="How it's built">
        <ul>
          <li>Frontend: React, Vite, Tailwind and Framer Motion.</li>
          <li>API: Express on Vercel, with Firebase Auth, MongoDB and Stripe Checkout.</li>
          <li>Parser: Python with pandas. It cleans the raw log, rebuilds each hand street by street, and computes player metrics and per-hand results.</li>
        </ul>
      </Block>
      <Block heading="Get in touch">
        <p>
          Questions and feedback are welcome. See the <PageLink page="contact" setCurrentPage={setCurrentPage}>contact page</PageLink>,
          or connect on <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>.
        </p>
      </Block>
    </InfoLayout>
  );
}

export function Contact() {
  return (
    <InfoLayout title="Contact" intro="Found a bug, have a feature idea, or need help with your account? Here's how to reach us.">
      <Block heading="Support and feedback">
        <p>To report a bug or ask a question, {contactLine}. Include the page you were on and what you expected to happen.</p>
      </Block>
      <Block heading="Elsewhere">
        <p>
          Code: <a href={SITE.github} target="_blank" rel="noopener noreferrer">GitHub</a>. Founder:{' '}
          <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer">{SITE.founder} on LinkedIn</a>.
        </p>
      </Block>
    </InfoLayout>
  );
}

const FAQS = [
  {
    q: 'How do I get my log out of PokerNow?',
    a: 'In your PokerNow game, open the log and download the full log as a CSV. For settle-ups, download the ledger CSV from the ledger screen instead.',
  },
  {
    q: 'What name do I enter when I upload?',
    a: 'Your PokerNow nickname, which is the part before the @ in the log. Win/loss results are calculated for that player.',
  },
  {
    q: 'What do VPIP, PFR and 3-bet mean?',
    a: 'VPIP is how often a player puts money in the pot. PFR is how often they raise before the flop. 3-bet is how often they re-raise a raise. Each is shown as a percentage of the hands that player was dealt.',
  },
  {
    q: 'How does the settle-up work?',
    a: 'Upload the ledger CSV, merge any duplicate names, and choose cents or dollars. The biggest losers are matched with the biggest winners so the group settles in as few transfers as possible. You can save the result and share it with a link.',
  },
  {
    q: 'Is it free?',
    a: 'Yes. Parsing, player stats, your top hands, settle-ups and bankroll tracking are free. Premium adds every showdown hand and the raise and 3-bet charts. You can try Premium free for 14 days without a credit card.',
  },
  {
    q: 'Who can see my data?',
    a: 'Your analyses, ledgers and bankroll are tied to your account. A ledger you share can be viewed by anyone with its link.',
  },
  {
    q: 'Is this made by PokerNow?',
    a: `No. ${SITE.name} is an independent tool by ${SITE.company} and is not affiliated with, endorsed by, or associated with PokerNow.club.`,
  },
];

export function Faq() {
  return (
    <InfoLayout title="FAQ" intro="Short answers to the questions we get most.">
      <dl className="divide-y divide-pn-line/10 border-y border-pn-line/10">
        {FAQS.map(item => (
          <div key={item.q} className="grid gap-2 py-7 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-10">
            <dt className="text-[1.05rem] font-bold">{item.q}</dt>
            <dd className="pn-body">{item.a}</dd>
          </div>
        ))}
      </dl>
    </InfoLayout>
  );
}

export function Privacy() {
  return (
    <InfoLayout title="Privacy" intro={`How ${SITE.company} handles your information. Last updated ${UPDATED}.`}>
      <Block heading="What we collect">
        <ul>
          <li>Account details: your email, display name and, if you sign in with Google, your profile photo.</li>
          <li>Files you upload: PokerNow logs and ledgers, and the analyses we generate from them. Logs include the nicknames of the other players in your game.</li>
          <li>Bankroll sessions you enter, and your plan, trial and payment status.</li>
          <li>Anonymous page-view analytics through Vercel Analytics.</li>
        </ul>
      </Block>
      <Block heading="How we use it">
        <p>We use your information to run your account, process your uploads, show your results, and provide Premium features. We don&rsquo;t sell your information.</p>
      </Block>
      <Block heading="Services we rely on">
        <ul>
          <li>Firebase Authentication for sign-in.</li>
          <li>MongoDB for storing your account data, uploads and results.</li>
          <li>Stripe for payments. Card details go to Stripe and never reach our servers.</li>
          <li>Vercel for hosting and analytics.</li>
        </ul>
      </Block>
      <Block heading="Sharing">
        <p>Ledgers are private unless you share them. A shared ledger can be viewed by anyone who has its link.</p>
      </Block>
      <Block heading="Your choices">
        <p>You can delete saved ledgers and bankroll sessions in the app. To delete your account and data, {contactLine}.</p>
      </Block>
    </InfoLayout>
  );
}

export function Terms({ setCurrentPage }) {
  return (
    <InfoLayout title="Terms" intro={`The terms for using ${SITE.name}, operated by ${SITE.company}. Last updated ${UPDATED}.`}>
      <Block heading="Using the service">
        <p>You need an account to upload logs and save results. Only upload files you have the right to use, and keep your sign-in details secure.</p>
      </Block>
      <Block heading="Free plan, trial and Premium">
        <p>
          The free plan is free. You can start one 14-day Premium trial without a credit card, and it ends on its own.
          Premium is a one-time payment processed by Stripe that unlocks Premium features on your account. See{' '}
          <PageLink page="payment" setCurrentPage={setCurrentPage}>pricing</PageLink> for current details.
        </p>
      </Block>
      <Block heading="Analytics are informational">
        <p>Results come from your logs and may contain errors. They are for your own review and aren&rsquo;t financial or gambling advice.</p>
      </Block>
      <Block heading="Not affiliated with PokerNow">
        <p>{SITE.name} is independent and is not affiliated with, endorsed by, or associated with PokerNow.club.</p>
      </Block>
      <Block heading="Changes and contact">
        <p>We may update these terms and will change the date above when we do. Questions: {contactLine}.</p>
      </Block>
    </InfoLayout>
  );
}
